import { RootState } from "@/store/types";
import { GetterTree } from "vuex";
import { SiteMonitorState, EventShareUser } from "./types";
import { Layout } from "@/types/Layout";

export const getters: GetterTree<SiteMonitorState, RootState> = {
	getLastActivity: state => state.LastActivity,
	getEventDetails: state => state.EventDetails,
	getEventLocationLastSet: state => state.eventLocationLastSet,
	getRequestingEventRecords: state => state.requestingEventRecords,
	getEventRecords: state => state.EventRecords,
	getEventRecordsAwaitingAckCount: state => state.EventRecordsAwaitingAckCount,
	getAcknowledgeAllEventRecordId: state => state.AcknowledgeAllEventRecordId,
	getLastAckAllEventRecord: state => state.LastAckAllEventRecordID,
	getIsEventRecordAwaitingAck: state => (eventRecordID: number): boolean => {
		return state.EventRecordsAwaitingAck && state.EventRecordsAwaitingAck[eventRecordID.toString()]?.awaitingAcknowledgement
	},
	getEventRecordsAwaitingRestoreCount: state => state.EventRecordsAwaitingRestoreCount,
	getEventRecordsWithFiles: state => state.EventRecordsWithFiles,
	getActiveResponseIDs: state => state.ActiveResponseIDs,
	getEventRecordFilters: state => state.EventRecordFilters,
	getCurrentRecordFilter: state => {
		if (state.EventRecordsFilterActiveIndex === -1) {
			return null;
		}

		return state.EventRecordFilters[state.EventRecordsFilterActiveIndex];
	},
	getEventRecordsNoFilterRenderIndex: state => state.EventRecordsNoFilterRenderIndex,
	getFilteredRecords: state => {
		if (state.EventRecordsFilterActiveIndex === -1) {
			return state.EventRecords;
		}

		const activeFilter = state.EventRecordFilters[state.EventRecordsFilterActiveIndex];
		return state.EventRecords.filter(
			(record: any) => activeFilter.filterRecordIds.indexOf(record.eventRecordTypeID) > -1
		);
	},
	getLatestEventRecord: state => state.LatestEventRecordID,
	getEventCloseShown: state => state.EventCloseOpen,
	getEventDetailsShown: state => state.EventDetailsOpen,
	getEventRaiseShown: state => state.EventRaiseOpen,
	getIncidentReportShown: state => state.IncidentReportOpen,
	getIncidentReportDownloaded: state => state.IncidentReportDownloaded,
	getEventShareShown: state => state.EventSharingOpen,
	getEventLeaveShown: state => state.EventLeaveOpen,
	getEventLeaveNote: state => state.EventLeaveNote,
	getPutEventOnTestShown: state => state.PutEventOnTestOpen,
	getInvolvedPartiesShown: state => state.InvolvedPartyOpen,
	getEventShare: state => state.EventShares,
	getEventShareSet: state => state.EventSharesSet,
	getEventNoPermission: state => state.EventNoPermission,
	userShares: (state, storeGetters) => {
		const shares: { [key: string]: EventShareUser } = {};
		storeGetters.getEventShare.forEach((user: EventShareUser) => {
			shares[user.eventUserID] = {
				isController: user.userID === state.ControllerUserID,
				...user
			};
		});

		return shares;
	},
	shareWithPersons: state => state.ShareWithPersons,
	getExternalUsersAllowed: state => state.ExternalUsersAllowed,
	getAdhocShareDetails: state => (eventUserId: string) => state.AdhocShareDetails[eventUserId],
	groupID: state => state.GroupID,
	getSelectedEventRecord: state => state.SelectedEventRecord,
	getActiveMapItems: state => state.activeMapItems,
	getMapCircleCenter: state => state.mapCircleCenter,
	getIsController: state => state.IsController,
	getUserID: state => state.UserID,
	filterIndex: state => state.EventRecordsFilterActiveIndex,
	getAuditService: state => state.auditService,
	getMaskReasons: state => state.MaskReasons,
	getEvents: state => state.Events,
	getGroupMaskReasons: state => state.GroupMaskReasons,
	maskedAlarms: state => state.MaskedAlarms,
	getEventOperators: state => state.EventShares,
	areaNotesVisible: state => state.AreaNotesVisible,
	getForceRestoreEventRecord: state => state.ForceRestoreEventRecord,
	getActiveMapItemsRequired: state => state.ActiveMapItemsRequired,
	getIsUploadingFile: state => state.IsUploadingFile,
	getHideMapFlag: state => state.hideMapFlag,
	getIsCameraEnabled: (state, getters, storeGetters) => (deviceId: number): boolean => {
		if (!!storeGetters && !!storeGetters.featuresList && !!storeGetters.featuresList.SystemView) {
			return true;
		}

		let isEnabled = true;

		if (!deviceId) {
			return isEnabled;
		}

		const serverId = state.deviceServerDetails.firstOrDefault(details => details.deviceId === deviceId)?.serverId;
		if (serverId) {
			const applianceServer = state.applianceServerDetails.firstOrDefault(appServer => appServer.serverId === serverId);
			if (!!applianceServer && !!applianceServer.applianceId) {
				isEnabled = applianceServer.isOnline;
			}
		}

		return isEnabled;
	},
	getApplianceOfflineNotification: state => (deviceId: number): string => {
		if (!deviceId) {
			return "";
		}

		let notification = "The Ops Link for this camera is currently offline.";

		const serverId = state.deviceServerDetails.firstOrDefault(details => details.deviceId === deviceId)?.serverId;
		if (serverId) {
			const applianceServer = state.applianceServerDetails.firstOrDefault(appServer => appServer.serverId === serverId);
			if (!!applianceServer && !!applianceServer.applianceTitle) {
				notification = `The Ops Link, '${applianceServer.applianceTitle}', for this camera is currently offline.`;
			}
		}

		return notification;
	},
	getSystemViewApplianceNotification: (state, getters: any) => (deviceId: number): string => {
		const serverId = state.deviceServerDetails.firstOrDefault(details => details.deviceId === deviceId)?.serverId;
		if (serverId) {
			const applianceServer = state.applianceServerDetails.firstOrDefault(appServer => {
			    return appServer.serverId === serverId
			});

			if (!applianceServer || applianceServer.applianceId == null || applianceServer.isOnline) {
				return null;
			}

			return `The Ops Link, '${applianceServer.applianceTitle}', for this camera is currently offline.`;
		}
		return null;
	},
	eventOutcomesTree: state => state.eventOutcomes.eventOutcomeTree,
	eventOutcomesFlatList: state => state.eventOutcomes.eventOutcomeFlatList,
	notificationModalLayout: (state): Layout => {
		const layoutString = state?.EventDetails?.notificationModalLayout;

		const defaultLayout: Layout = {
				left: ["contacts", "contact-groups", "notification-note", "event-outcome"],
				center: [],
				right: ["notification-templates", "custom-fields", "attach-sit-rep"]
			};

		// Load all the default components into a single list that will be used for validating the layout later
		const requiredComponents = defaultLayout.left.concat(defaultLayout.center, defaultLayout.right);

		if (layoutString) {
			try {
				const eventLayout = JSON.parse(layoutString);

				// Handle any duplicated components in each column
				eventLayout.left = Array.from(new Set(eventLayout.left ?? []));
				eventLayout.center = Array.from(new Set(eventLayout.center ?? []));
				eventLayout.right = Array.from(new Set(eventLayout.right ?? []));

				// Get all the components for the events layout into one list.
				const eventComponents = eventLayout.left.concat(eventLayout.center, eventLayout.right);

				// Check for any components that are duplicated across columns, and remove the duplicates
				const duplicatedEventComponents = Array.from(new Set(eventComponents
					.filter((item: string, idx: number, a: string[]) => a.indexOf(item) !== idx)));

				// When removing duplicates, keep the component in the leftmost column
				if (duplicatedEventComponents.length > 0) {
					for(const duplicate of duplicatedEventComponents) {
						if (eventLayout.left.includes(duplicate)) {
							eventLayout.center = eventLayout.center.filter((item: string) => item !== duplicate);
							eventLayout.right = eventLayout.right.filter((item: string) => item !== duplicate);
						}

						if (eventLayout.center.includes(duplicate)) {
							eventLayout.right = eventLayout.right.filter((item: string) => item !== duplicate);
						}
					}
				}

				// Ensure all the required components are present in the layout, and if not, add them back to their default column
				for (const component of requiredComponents) {
					if (!eventComponents.includes(component)) {
						if (defaultLayout.left.includes(component)) {
							eventLayout.left.push(component);
						} else if (defaultLayout.center.includes(component)) {
							eventLayout.center.push(component);
						} else if (defaultLayout.right.includes(component)) {
							eventLayout.right.push(component);
						}
					}
				}

				return eventLayout;
			} catch (error) {
				console.error("Error parsing notification modal layout from event details", error);
				return defaultLayout;
			}
		}

		return defaultLayout;
	},
	notificationModalLayoutColumnCount: (_state, getters): number => {
		const layout = getters.notificationModalLayout;
		const arrays = [layout.left, layout.center, layout.right];
		const nonEmptyCount = arrays.filter((arr: any[]) => Array.isArray(arr) && arr.length > 0).length;
		return nonEmptyCount;
	},
	notificationRulesTooltip: (state): string => {
		const triggeredRules = state.notificationRulesContributingToNotification;
		if (triggeredRules.length === 0) {
			return "";
		}

		if (triggeredRules.every(rule => rule.notificationTemplate === null)) {
			return "These contacts were applied by the following rules: " + triggeredRules.map(rule => rule.title).join(", ");
		}

		let tooltip = "";
		tooltip += "This template and its contacts were applied by the following rules:\n";
		tooltip += triggeredRules.filter(rule => rule.notificationTemplate !== null).map(rule => `• ${rule.title}`).join("\n");

		if (triggeredRules.some(rule => rule.contactGroupIds || rule.userIds || rule.emailAddresses)) {
			tooltip += "\n Additional contacts were applied by the following rules:\n";
			tooltip += `${triggeredRules
				.filter(rule => rule.contactGroupIds || rule.userIds || rule.emailAddresses)
				.map(rule => `• ${rule.title}`).join("\n")}`;
		}
		return tooltip;
	}
};
